import React from 'react';
import { baseXUnit, breakpoints, color } from '../../styles/theme';
import { t } from '../../utils/i18n';
import {
  formatImtTripPhaseCitiesToStrings,
  getCityNameOfHotelPhase
} from '../common/sticky-pricebox/tripInfoUtils';
import RenderAsync from '../common/RenderAsync';
import { BoxSkeleton } from '../common/skeletons';
import {
  IMTTripPhaseCities,
  IMTTripReservingDetails
} from '../../integrations/erp/apiTypes';
import { LoadingState } from '../../loading/loadable';
import Row from '../grid/Row';
import Col from '../grid/Col';
import Spacing from '../common/Spacing';
import TripsAvailable from '../common/sticky-pricebox/TripsAvailable';
import H from '../elements/H';
import TimMessage from '../tim/TimMessage';
import CardContainer from '../common/CardContainer';
import TripContentElement from './TripContentElement';
import TripPricingInformation from './TripPricingInformation';

interface TripContentCardProps {
  imtTripPhaseCities: IMTTripPhaseCities[];
  tripReservationDataLoadingState: LoadingState;
  tripReservationData: IMTTripReservingDetails | undefined;
  shipTrip: boolean;
}

const TripContentCard: React.FunctionComponent<
  TripContentCardProps & React.HTMLAttributes<HTMLElement>
> = ({
  imtTripPhaseCities,
  tripReservationDataLoadingState,
  tripReservationData,
  shipTrip
}) => {
  const hotelPhaseCityName = getCityNameOfHotelPhase(imtTripPhaseCities);
  const travelPhaseCityNames =
    formatImtTripPhaseCitiesToStrings(imtTripPhaseCities);
  const renderNoPrice = () => {
    return (
      <Spacing top={[0, 0, 4, 2]}>
        <H styling="h1" tag="div" color="light" data-testid="no-price-message">
          <strong>___{t(' € / henkilö')}</strong>
        </H>
      </Spacing>
    );
  };
  const renderTimErrorMessage = () => {
    return (
      <Spacing top={2}>
        <TimMessage>
          {t('Valitettavasti tämä matka ei ole juuri nyt saatavilla 😞')}
        </TimMessage>
      </Spacing>
    );
  };

  return (
    <CardContainer data-testid="trip-content-card">
      <Row>
        <Col size={[24, 24, 12]}>
          {travelPhaseCityNames && hotelPhaseCityName && (
            <div
              className="trip-phase-information"
              data-testid="trip-phase-information"
            >
              <Spacing bottom={2}>
                <TripContentElement
                  label={shipTrip ? t('Laivamatka') : t('Lennot')}
                  icon={shipTrip ? 'travel-ship' : 'travel-plane'}
                  textLines={formatImtTripPhaseCitiesToStrings(
                    imtTripPhaseCities
                  )}
                  reassuringText={
                    shipTrip
                      ? t('Valitse sinulle sopivimmat lähdöt!')
                      : t('Max. 1 vaihto — Valitse sinulle sopivin lento!')
                  }
                  data-testid="trip-travel-phases"
                />
              </Spacing>
              <Spacing bottom={1}>
                <TripContentElement
                  label={t('Hotelli')}
                  icon="travel-hotel"
                  textLines={[t('Kohteessa') + ' ' + hotelPhaseCityName]}
                  reassuringText={t(
                    'Vähintään 3 tähden hotelli — Valitse sinulle sopivin majoitus!'
                  )}
                  data-testid="trip-hotel-phase"
                />
              </Spacing>
            </div>
          )}
        </Col>
        <Col size={[24, 24, 12]}>
          <div className="pricing-info">
            <div>
              <RenderAsync
                state={tripReservationDataLoadingState}
                onLoading={() => {
                  return (
                    <BoxSkeleton
                      uniqueKey={'pricing-info-skeleton'}
                      height={[baseXUnit(8), baseXUnit(8), baseXUnit(11)]}
                    />
                  );
                }}
                onError={renderNoPrice}
              >
                {tripReservationData && (
                  <TripPricingInformation
                    tripReservationData={tripReservationData}
                  />
                )}
              </RenderAsync>
              <Spacing top={1}>
                <RenderAsync
                  state={tripReservationDataLoadingState}
                  onLoading={() => {
                    return (
                      <div>
                        <BoxSkeleton
                          uniqueKey={'availability-skeleton'}
                          height={[
                            baseXUnit(2.5),
                            baseXUnit(2.5),
                            baseXUnit(3)
                          ]}
                          width={['80%', '80%', '90%']}
                        />
                      </div>
                    );
                  }}
                  onError={renderTimErrorMessage}
                >
                  {tripReservationData && (
                    <TripsAvailable date={tripReservationData.date} />
                  )}
                </RenderAsync>
              </Spacing>
            </div>
          </div>
        </Col>
      </Row>
      {/*language=CSS*/}
      <style jsx>{`
        .trip-phase-information {
          border-bottom: 1px dashed ${color.light};
          padding: ${baseXUnit(2)};
        }
        .pricing-info {
          padding: ${baseXUnit(2)};
          height: 100%;
          display: flex;
          align-items: center;
        }
        @media ${breakpoints.large} {
          .trip-phase-information {
            border-bottom: 0;
            border-right: 1px dashed ${color.light};
            padding: ${baseXUnit(3)};
          }
          .pricing-info {
            border-top: 0;
            padding: ${baseXUnit(3)};
            padding-left: ${baseXUnit(1)};
          }
        }
      `}</style>
    </CardContainer>
  );
};

export default TripContentCard;
